import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, Card, Chip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import FileTypeIcon, { fileTypeForContent } from '../common/FileTypeIcon';
import TaskStatusChip from './TaskStatusChip';
import { boardColumns, formatDue, rowAssignee, rowColumnId, rowDue, rowKey, shortName, stageKeysByType, upcomingLabel, WAITING_COLUMN } from './taskUi';

const COLUMN_WIDTH = 260;

// upcoming: the task hasn't reached this column's stage yet; shown faded and can't be dragged
const BoardCard = ({ row, onOpen, dragHandleProps, draggableProps, innerRef, dragging, upcoming = false }) => (
    <Card
        ref={innerRef}
        {...draggableProps}
        {...dragHandleProps}
        variant="outlined"
        onClick={() => onOpen(row)}
        // A waiting card can't be dragged, so it gets no drag handle's focus
        {...(row.waitingForFile && {
            role: 'button',
            tabIndex: 0,
            onKeyDown: (e) => {
                if (e.key === 'Enter') onOpen(row);
            },
        })}
        sx={{
            p: 1.25,
            mb: 1,
            cursor: 'pointer',
            boxShadow: dragging ? 4 : 0,
            opacity: upcoming ? 0.7 : 1,
            borderStyle: upcoming ? 'dashed' : 'solid',
            '&:hover': { borderColor: 'primary.main', opacity: 1 },
            ...draggableProps?.style,
        }}
    >
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
            <FileTypeIcon type={fileTypeForContent(row.contentType)} />
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', flex: 1 }}>
                {row.contentTitle}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mt: 0.75 }}>
            <Chip size="small" label={row.languageLabel} sx={{ fontSize: '0.72rem' }} />
            <TaskStatusChip row={row} />
        </Box>
        {!row.redacted && !row.waitingForFile && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                {rowAssignee(row) ? shortName(rowAssignee(row)) : upcoming ? 'Not planned' : 'Unassigned'}
                {rowDue(row) ? ` · due ${formatDue(rowDue(row))}` : ''}
            </Typography>
        )}
    </Card>
);

/**
 * Pipeline as a board: one column per stage, one card per file + language. PMs drag a card to
 * another column to move the task there; the drawer confirms the move (forward skips the
 * stages in between, backward is a send-back with a reason). Everyone else sees it read-only.
 * With upcoming rows, each column also lists, faded, the tasks that haven't reached it yet.
 * Column headers use the server's group counts when given, so they stay right past the card cap.
 */
const TaskBoard = ({ rows, groups, taskTypes, contentType, canMove, onOpen, onMove }) => {
    const columns = useMemo(() => boardColumns(taskTypes, rows, contentType), [taskTypes, rows, contentType]);
    const stagesByType = useMemo(() => stageKeysByType(taskTypes), [taskTypes]);
    const byStage = useMemo(() => {
        const map = new Map(columns.map((c) => [c.id, { now: [], upcoming: [] }]));
        rows.forEach((r) => map.get(rowColumnId(r))?.[r.extras?.upNext ? 'upcoming' : 'now'].push(r));
        return map;
    }, [columns, rows]);
    // Server counts are per stage key; a key split into several named columns counts its cards instead
    const countsByStage = useMemo(() => {
        const split = new Set(columns.filter((c, _, all) => all.filter((o) => o.stageKey === c.stageKey).length > 1).map((c) => c.stageKey));
        return new Map((groups || []).filter((g) => !split.has(g.key)).map((g) => [g.key, g]));
    }, [groups, columns]);
    const draggableRow = (id) => rows.find((r) => r.id === id && !r.extras?.upNext);
    const [dragging, setDragging] = useState(null);

    const allowed = (col) => {
        if (!dragging) return true;
        const keys = stagesByType[dragging.taskTypeKey];
        return !keys || keys.has(col.id);
    };

    const onDragEnd = ({ draggableId, source, destination }) => {
        setDragging(null);
        if (!destination || destination.droppableId === source.droppableId) return;
        const row = draggableRow(draggableId);
        const col = columns.find((c) => c.id === destination.droppableId);
        if (row && col) onMove(row, col.stageKey);
    };

    return (
        <DragDropContext
            onDragStart={({ draggableId }) => setDragging(draggableRow(draggableId) || null)}
            onDragEnd={onDragEnd}
        >
            <Box
                role="list"
                aria-label="Pipeline board"
                sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, alignItems: 'flex-start' }}
            >
                {columns.map((col) => {
                    const { now: cards, upcoming } = byStage.get(col.id) || { now: [], upcoming: [] };
                    const counts = countsByStage.get(col.stageKey);
                    const disabled = !canMove || col.id === WAITING_COLUMN.id || !allowed(col);
                    return (
                        <Box
                            key={col.id}
                            role="listitem"
                            sx={{
                                width: COLUMN_WIDTH,
                                flex: `0 0 ${COLUMN_WIDTH}px`,
                                backgroundColor: 'action.hover',
                                borderRadius: 1,
                                p: 1,
                                opacity: dragging && disabled ? 0.45 : 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, px: 0.5, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                    {col.displayName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {counts ? counts.count : cards.length}
                                    {upcomingLabel(counts) ? ` · ${upcomingLabel(counts)}` : ''}
                                </Typography>
                            </Box>
                            <Droppable droppableId={col.id} isDropDisabled={disabled}>
                                {(provided, snapshot) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        sx={{
                                            minHeight: 60,
                                            borderRadius: 1,
                                            outline: snapshot.isDraggingOver ? '2px dashed' : 'none',
                                            outlineColor: 'primary.main',
                                        }}
                                    >
                                        {cards.map((row, index) => (
                                            <Draggable
                                                key={row.id}
                                                draggableId={row.id}
                                                index={index}
                                                isDragDisabled={!canMove || row.overallStatus === 'CANCELLED' || !!row.waitingForFile}
                                            >
                                                {(p, s) => (
                                                    <BoardCard
                                                        row={row}
                                                        onOpen={onOpen}
                                                        innerRef={p.innerRef}
                                                        draggableProps={p.draggableProps}
                                                        dragHandleProps={p.dragHandleProps}
                                                        dragging={s.isDragging}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        {upcoming.map((row) => (
                                            <BoardCard key={rowKey(row)} row={row} onOpen={onOpen} upcoming />
                                        ))}
                                        {cards.length === 0 && upcoming.length === 0 && !snapshot.isDraggingOver && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                component="div"
                                                sx={{ textAlign: 'center', py: 2 }}
                                            >
                                                Nothing here
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Droppable>
                        </Box>
                    );
                })}
            </Box>
        </DragDropContext>
    );
};

export default TaskBoard;
